import React, { useEffect, useState } from "react";
import { LogOut, UserCircle } from 'lucide-react';
import { AnimatePresence, motion } from "framer-motion";
import "../../styles/Profile.css";
import { RootState } from "../../redux/reducers";
import { Navigate } from 'react-router-dom';
import { Building, Download, Geo, Gear, X } from "react-bootstrap-icons";
import { Col, Row, Spinner } from "react-bootstrap";
import { indicadoresActions } from "../../redux/actions/Otros/indicadoresActions";
import { logout } from "../../redux/actions/auth/authActions";
import { connect, useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import Preferencias from "../Configuracion/Preferencias";


interface Roles {
  NombreRol: string;
  Descripcion: string;
  IdRol: number;
  IdAplicacion: number;
  codigoEstablecimiento: number;
  nombreEstablecimiento: string;
}

export interface Objeto {
  IdCredencial: number;
  Nombre: string;
  Apellido1: string;
  Apellido2: string;
  Correo: string;
  Roles: Roles[];
  Establecimiento: number;
  usr_run: string;
  error: string | null;
  isAuthenticated: boolean;
}

export interface IndicadoresProps {
  valor: string | number;
}

interface ProfileProps {
  logout: () => void;
  indicadoresActions: () => Promise<boolean>;
  habilitados: Record<
    "UTM" | "UF" | "Dólar" | "IPC" | "BTC" | "EURO" | "Ethereum",
    boolean
  >;
  objeto: Objeto;
  utm: IndicadoresProps;
  uf: IndicadoresProps;
  dolar: IndicadoresProps;
  bitcoin: IndicadoresProps;
  ipc: IndicadoresProps;
  btc: IndicadoresProps;
  euro: IndicadoresProps;
  Ethereum: IndicadoresProps;
  isDarkMode: boolean;
  token: string | null;
  origenLogin: number;
  activo?: string;
}

const Profile: React.FC<ProfileProps> = ({
  logout,
  indicadoresActions,
  habilitados,
  objeto,
  utm,
  uf,
  dolar,
  ipc,
  btc,
  euro,
  Ethereum,
  isDarkMode,
  token,
  origenLogin,
  activo = "General"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const togglePanel = () => {
    setIsOpen((prev) => !prev);
  };

  const cargaIndicadores = async () => {
    if (uf.valor === 0 && utm.valor === 0 && dolar.valor === 0 && ipc.valor === 0) {
      setLoading(true);
      const resultado = await indicadoresActions();
      if (resultado) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (token) {
      cargaIndicadores();
    }
  }, [indicadoresActions]);

  const handleLogout = () => {
    if (origenLogin === 0) {
      dispatch(logout());
      const redirectUrl = import.meta.env.VITE_ORIGEN_LOGIN;
      window.location.href = redirectUrl;
      return;
    } else {
      dispatch(logout());
      return <Navigate to="/" />;
    }
  };

  const PrimeraMayuscula = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const panelVariants = {
    initial: { opacity: 0, x: 100 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 100 },
  };

  const panelTransition = {
    type: "tween",
    easeOut: [0, 0, 0.58, 1],
    duration: 0.2
  };

  const indicadoresData = [
    {
      id: "UTM",
      title: "UTM",
      value: `$${Number(utm.valor).toLocaleString("es-CL")}`,
      habilitado: habilitados.UTM,
    },
    {
      id: "UF",
      title: "UF",
      value: `$${Number(uf.valor).toLocaleString("es-CL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      habilitado: habilitados.UF,
    },
    {
      id: "Dólar",
      title: "Dólar",
      value: `$${Number(dolar.valor).toLocaleString("es-CL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      habilitado: habilitados["Dólar"],
    },
    {
      id: "IPC",
      title: "IPC",
      value: `${Number(ipc.valor).toLocaleString("es-CL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
      habilitado: habilitados.IPC,
    },
    {
      id: "BTC",
      title: "BTC",
      value: Number(btc.valor).toLocaleString("es-CL"),
      habilitado: habilitados.BTC,
    },
    {
      id: "EURO",
      title: "EURO",
      value: `$${Number(euro.valor).toLocaleString("es-CL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      habilitado: habilitados.EURO,
    },
    {
      id: "Ethereum",
      title: "Ethereum",
      value: `$${Number(Ethereum.valor).toLocaleString("es-CL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      habilitado: habilitados.Ethereum,
    },
  ];

  const noDisponible = (valor: string | number): boolean => {
    if (valor === null || valor === undefined || String(valor).trim() === "") {
      return true;
    }

    const texto = String(valor).trim();

    const numero = Number(
      texto.includes(",")
        ? texto.replace(/\./g, "").replace(",", ".")
        : texto
    );

    return !Number.isFinite(numero) || numero === 0;
  };

  return (
    <>
      {/* Botón de perfil */}
      <div className="d-flex justify-content-end align-content-center p-1">
        <button
          type="button"
          onClick={togglePanel}
          className={`d-flex justify-content-end align-items-center rounded p-1 ${isDarkMode ? "text-light" : "text-dark"} nav-item nav-link`}
        >
          <UserCircle
            className={`${isDarkMode ? "text-white" : "text-muted"}`}
            size={30}
            aria-hidden="true"
          />
          <span className={`d-none d-md-inline ${isDarkMode ? "text-white" : ""}`}>
            <p className="fs-09em ms-1">
              {objeto?.Nombre && PrimeraMayuscula(objeto.Nombre)} {objeto?.Apellido1 && PrimeraMayuscula(objeto.Apellido1)}
            </p>
          </span>
        </button>
      </div>

      {/* Panel deslizante */}
      <AnimatePresence >

        {isOpen && (
          <motion.div
            className={`slide-panel-overlay  slide-panel ${isDarkMode ? "bg-color-dark" : "bg-light"}`}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={panelVariants}
            transition={panelTransition}
            onClick={togglePanel}
          >
            <motion.div onClick={(e) => e.stopPropagation()}>
              <div className="d-flex justify-content-end">
                <button
                  className={`btn p-0 ${isDarkMode ? "text-light" : "text-dark"}`}
                  onClick={togglePanel}
                  aria-label="Cerrar"
                >
                  <X size={35} />
                </button>
              </div>

              <div className="flex-grow-1 min-vh-100" style={{ maxWidth: "310px", minWidth: "310px" }}>
                {/* Nombre completo */}
                <div className="text-center fw-semibold fs-4 border-bottom mb-4">
                  <p className="fs-5">
                    {objeto?.Nombre && PrimeraMayuscula(objeto.Nombre)} {objeto?.Apellido1 && PrimeraMayuscula(objeto.Apellido1)} {objeto?.Apellido2 && PrimeraMayuscula(objeto.Apellido2)}
                  </p>
                </div>

                {/* Información del usuario */}
                <p className="mb-2 fw-normal fs-6 fs-md-5 fs-lg-4">
                  <strong>
                    <Building className="m-1 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    Dependencia:
                  </strong> {objeto.Roles[0]?.NombreRol}
                </p>

                <p className="mb-2 fw-normal fs-6 fs-md-5 fs-lg-4">
                  <strong>
                    <Geo className="m-1 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    Establecimiento:
                  </strong> {objeto.Roles[0]?.nombreEstablecimiento}
                </p>

                {/* Botón Configuración */}
                <button
                  onClick={() => setMostrarModal(true)}
                  className={`fw-normal p-1 border-bottom ${isDarkMode ? "text-light" : "text-dark"} nav-item nav-link mb-4 fs-6 fs-md-5 fs-lg-4 w-100 text-start p-0`}
                >
                  <strong>
                    <Gear className="m-1 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    Configuración
                  </strong>
                </button>

                {/* Manual de usuario */}
                <a
                  href="/manual_usuario.pdf"
                  download="manual_usuario.pdf"
                  className={`fw-normal p-1 border-bottom ${isDarkMode ? "text-light" : "text-dark"} nav-item nav-link mb-4 fs-6 fs-md-5 fs-lg-4 w-100 text-start p-0`}
                >
                  <strong>
                    <Download className="m-1 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                    Manual de usuario
                  </strong>
                </a>

                {/* Indicadores */}

                <Row className="g-2 mb-5" >
                  {indicadoresData
                    .filter(
                      (item) => item.habilitado && !noDisponible(
                        item.id === "UTM" ? utm.valor :
                          item.id === "UF" ? uf.valor :
                            item.id === "Dólar" ? dolar.valor :
                              item.id === "IPC" ? ipc.valor :
                                item.id === "BTC" ? btc.valor :
                                  item.id === "EURO" ? euro.valor :
                                    Ethereum.valor
                      )
                    )
                    .map((item, index) => (
                      <Col lg={indicadoresData.filter((item) => item.habilitado).length === 1 ? 12 : 6} md={6} sm={12} key={index}>
                        <div className="text-center bg-secondary p-1 text-white border-0 shadow-sm rounded h-100 d-flex flex-column align-items-center justify-content-center">
                          <div className="mb-1">
                            <strong className="no-cursor">{item.title}</strong>
                          </div>
                          {loading ? (
                            <Spinner className="fs-6" />
                          ) : (
                            <div className="fw-semibold no-cursor">{item.value}</div>
                          )}
                        </div>
                      </Col>
                    ))}
                </Row>

                {/* Botón Cerrar Sesión */}
                <button
                  onClick={handleLogout}
                  type="button"
                  className={`p-2 rounded ${isDarkMode ? "text-light" : "text-dark"} nav-item nav-link w-100 border-bottom rounded-0 fs-6 fs-md-5 fs-lg-4`}
                >
                  Cerrar Sesión
                  <LogOut className="ms-1 p-1 flex-shrink-0 h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

      </AnimatePresence>

      {/* Modal de Preferencias */}
      <Preferencias
        isDarkMode={isDarkMode}
        activo={activo}
        mostrarModal={mostrarModal}
        setMostrarModal={setMostrarModal}
      />

    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  objeto: state.validaApiLoginReducers,
  logout: state.loginReducer.logout,
  utm: state.indicadoresReducers.utm || 0,
  uf: state.indicadoresReducers.uf || 0,
  dolar: state.indicadoresReducers.dolar || 0,
  bitcoin: state.indicadoresReducers.bitcoin || 0,
  ipc: state.indicadoresReducers.ipc || 0,
  btc: state.indicadoresReducers.btc || 0,
  euro: state.indicadoresReducers.euro || 0,
  Ethereum: state.indicadoresReducers.Ethereum || 0,
  isDarkMode: state.darkModeReducer.isDarkMode,
  token: state.loginReducer.token,
  origenLogin: state.loginReducer.origenLogin,
  habilitados: state.actualizarIndicadorHabilitadoReducers.habilitados || [],
});

export default connect(mapStateToProps, {
  logout,
  indicadoresActions
})(Profile);